import React, { useState, useEffect } from 'react';
import { 
  Save, 
  X, 
  Phone,
  Mail,
  Upload, 
  MapPin, 
  DollarSign, 
  Users, 
  Calendar,
  Clock,
  Plus,
  Trash2,
  Star,
  Camera,
  Image as ImageIcon,
  Settings,
  Bed,
  Car,
  Building2,
  Wifi,
  Coffee,
  Tv,
  AirVent,
  ParkingCircle,
  UtensilsCrossed,
  Waves,
  TreePine,
  Dumbbell,
  Shield,
  Video,
  AlertCircle,
  CheckCircle,
  Edit3,
  Link,
  Globe
} from 'lucide-react';
import { FileUpload } from '../../common/FileUpload';
import { bookingService } from '../../../services/bookingService';
import { firestoreService } from '../../../services/firestoreService';

// Corrected Media Upload Section using proper ListingForm props
export const MediaTab = ({ 
  formData, 
  handleInputChange, 
  bookId, 
  uploadStates, 
  uploadErrors 
}) => {
  const [videoUrls, setVideoUrls] = useState(['', '']);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  // Process YouTube URL to get video ID and thumbnail
  const processYouTubeUrl = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        const videoId = match[1];
        return {
          url: url,
          videoId: videoId,
          thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
          title: 'YouTube Video',
          type: 'youtube',
          originalUrl: url
        };
      }
    }
    return null;
  };

  // Handle multiple image uploads using FileUpload component
  const handleImageFilesSelect = async (files, error) => {
    if (error) {
        alert(`Error selecting images: ${error}`);
        return;
    }

    if (!files || files.length === 0) return;

    const fileArray = Array.isArray(files) ? files : [files];
    
    // Check total image limit
    const currentImageCount = (formData.images || []).length;
    if (currentImageCount + fileArray.length > 20) {
        alert(`Maximum 20 images allowed. You can add ${20 - currentImageCount} more images.`);
        return;
    }

    setUploading(true);

    try {
        const results = await firestoreService.uploadMultipleFiles(
        fileArray,
        (file) => firestoreService.uploadFile(file, `pb-${bookId}/listings/images`),
        (current, total, result, error) => {
            setUploadProgress({ current, total });
            if (error) {
            console.error(`Upload error ${current}/${total}:`, error);
            }
        }
        );

        // Filter successful uploads and create image objects
        const successfulUploads = results.filter(result => result.success !== false);
        const newImageObjects = successfulUploads.map((result, index) => ({
        id: Date.now() + index, // Generate unique ID
        url: result.url,
        fileName: result.fileName || result.originalFileName || `image_${index}`,
        originalName: result.originalFileName || result.fileName || `image_${index}`,
        alt: '',
        featured: false,
        uploadedAt: new Date().toISOString()
        }));

        // Use handleInputChange to update images with object format
        handleInputChange('images', [...(formData.images || []), ...newImageObjects]);

        // Set first uploaded image as primary if no primary set
        if ((formData.primaryImageIndex === undefined || formData.primaryImageIndex === null) && newImageObjects.length > 0) {
        handleInputChange('primaryImageIndex', (formData.images || []).length);
        }

    } catch (error) {
        console.error('Error uploading images:', error);
        alert('Failed to upload some images. Please try again.');
    } finally {
        setUploading(false);
        setUploadProgress({ current: 0, total: 0 });
    }
    };

  // Handle video file uploads using FileUpload component
  const handleVideoFilesSelect = async (files, error) => {
    if (error) {
      alert(`Error selecting videos: ${error}`);
      return;
    }

    if (!files || files.length === 0) return;

    const fileArray = Array.isArray(files) ? files : [files];
    
    // Check video limit
    const currentVideoCount = (formData.videos || []).length;
    if (currentVideoCount + fileArray.length > 3) {
      alert(`Maximum 3 videos allowed. You can add ${3 - currentVideoCount} more videos.`);
      return;
    }

    setUploading(true);

    try {
      const results = await firestoreService.uploadMultipleFiles(
        fileArray,
        (file) => firestoreService.uploadFile(file, `pb-${bookId}/listings/videos`),
        (current, total, result, error) => {
          setUploadProgress({ current, total });
        }
      );

      const successfulUploads = results.filter(result => result.success !== false);
      const newVideos = successfulUploads.map(result => ({
        url: result.url,
        type: 'upload',
        fileName: result.fileName,
        fileType: result.fileType,
        thumbnail: null,
        addedAt: new Date().toISOString()
      }));

      // Use handleInputChange to update videos
      handleInputChange('videos', [...(formData.videos || []), ...newVideos]);

    } catch (error) {
      console.error('Error uploading videos:', error);
      alert('Failed to upload videos. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Add YouTube video URL
  const addYouTubeVideo = (index) => {
    const url = videoUrls[index].trim();
    if (!url) return;

    const processedVideo = processYouTubeUrl(url);
    if (!processedVideo) {
      alert('Invalid YouTube URL. Please use a valid YouTube video link.');
      return;
    }

    const currentVideos = formData.videos || [];
    if (currentVideos.length >= 3) {
      alert('Maximum 3 videos allowed per listing');
      return;
    }

    // Check if URL already exists
    const existingVideo = currentVideos.find(v => v.originalUrl === url);
    if (existingVideo) {
      alert('This YouTube video is already added');
      return;
    }

    // Use handleInputChange to update videos
    handleInputChange('videos', [...currentVideos, { 
      ...processedVideo, 
      addedAt: new Date().toISOString() 
    }]);
    
    // Clear the input
    const newUrls = [...videoUrls];
    newUrls[index] = '';
    setVideoUrls(newUrls);
  };

  // Remove image
  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    let newPrimaryIndex = formData.primaryImageIndex || 0;
    
    if (formData.primaryImageIndex === index) {
      newPrimaryIndex = 0;
    } else if (formData.primaryImageIndex > index) {
      newPrimaryIndex = formData.primaryImageIndex - 1;
    }

    handleInputChange('images', newImages);
    handleInputChange('primaryImageIndex', Math.min(newPrimaryIndex, Math.max(0, newImages.length - 1)));
  };

  // Remove video
  const removeVideo = (index) => {
    const newVideos = formData.videos.filter((_, i) => i !== index);
    handleInputChange('videos', newVideos);
  };

  // Set primary image
  const setPrimaryImage = (index) => {
    handleInputChange('primaryImageIndex', index);
  };

  return (
    <div className="space-y-8">
      {/* Images Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Images</h3>
            <p className="text-sm text-gray-600">Add up to 20 high-quality images of your listing</p>
          </div>
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {(formData.images || []).length} / 20 images
          </span>
        </div>

        {/* Image Upload using FileUpload Component */}
        <div className="mb-6">
          <FileUpload
            onFileSelect={handleImageFilesSelect}
            accept="image/*"
            maxSize={10}
            label="Upload Images"
            uploading={uploading}
            multiple={true}
            showPreview={false}
          />
          
          <p className="text-xs text-gray-500 mt-2">
            Supported formats: JPEG, PNG, WebP, GIF. Maximum 10MB per image.
          </p>
        </div>

        {/* Upload Progress */}
        {uploading && uploadProgress.total > 0 && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between text-sm text-blue-700 mb-2">
              <span className="font-medium">Uploading images...</span>
              <span>{uploadProgress.current} / {uploadProgress.total}</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Image Gallery */}
        {formData.images && formData.images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {formData.images.map((image, index) => {
            const imageUrl = typeof image === 'string' ? image : image.url;
            const imageAlt = typeof image === 'string' ? `Image ${index + 1}` : (image.alt || `Image ${index + 1}`);
            
            return (
                <div key={index} className="relative group">
                <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
                    <img
                    src={imageUrl}
                    alt={imageAlt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                </div>
                
                {/* Image Controls Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl">
                    <div className="absolute top-2 left-2 flex gap-1">
                    {formData.primaryImageIndex === index ? (
                        <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full font-medium">
                        Primary
                        </span>
                    ) : (
                        <button
                        onClick={() => setPrimaryImage(index)}
                        className="px-2 py-1 bg-gray-800 bg-opacity-75 text-white text-xs rounded-full hover:bg-opacity-100 transition-all"
                        >
                        Set Primary
                        </button>
                    )}
                    </div>
                    <button
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                    >
                    <X className="h-3 w-3" />
                    </button>
                </div>
                
                {/* Image Number Badge */}
                <div className="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-75 text-white text-xs rounded-full">
                    {index + 1}
                </div>
                </div>
            );
            })}
        </div>
        )}
      </div>

      {/* Videos Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Videos</h3>
            <p className="text-sm text-gray-600">Upload video files or add YouTube links</p>
          </div>
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {(formData.videos || []).length} / 3 videos
          </span>
        </div>

        {/* Video Upload from Device using FileUpload Component */}
        <div className="mb-6">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Video className="h-4 w-4" />
            Upload Video Files
          </h4>
          
          <FileUpload
            onFileSelect={handleVideoFilesSelect}
            accept="video/*"
            maxSize={50}
            label="Upload Videos"
            uploading={uploading}
            multiple={true}
            showPreview={false}
          />
          
          <p className="text-xs text-gray-500 mt-2">
            Supported formats: MP4, WebM, MOV, AVI. Maximum 50MB per video.
          </p>
        </div>

        {/* YouTube URL Input */}
        <div className="mb-6">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <div className="w-5 h-5 bg-red-600 rounded flex items-center justify-center">
              <div className="w-0 h-0 border-l-2 border-l-white border-y-2 border-y-transparent ml-0.5" />
            </div>
            Add YouTube Videos
          </h4>
          
          <div className="space-y-3">
            {videoUrls.map((url, index) => (
              <div key={index} className="flex gap-3">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => {
                    const newUrls = [...videoUrls];
                    newUrls[index] = e.target.value;
                    setVideoUrls(newUrls);
                  }}
                  placeholder={`https://www.youtube.com/watch?v=... (Video ${index + 1})`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={() => addYouTubeVideo(index)}
                  disabled={!url.trim() || uploading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add
                </button>
              </div>
            ))}
          </div>
          
          <p className="text-xs text-gray-500 mt-2">
            Paste YouTube video URLs to embed them in your listing
          </p>
        </div>

        {/* Video Gallery */}
        {formData.videos && formData.videos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formData.videos.map((video, index) => (
              <div key={index} className="relative group bg-gray-100 rounded-xl overflow-hidden">
                {video.type === 'youtube' ? (
                  <div className="aspect-video relative">
                    <img
                      src={video.thumbnail}
                      alt="YouTube video thumbnail"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                        <div className="w-0 h-0 border-l-8 border-l-white border-y-6 border-y-transparent ml-1" />
                      </div>
                    </div>
                    {/* YouTube branding */}
                    <div className="absolute bottom-3 right-3">
                      <div className="px-2 py-1 bg-red-600 text-white text-xs rounded font-medium">
                        YouTube
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-video bg-gray-200 flex items-center justify-center">
                    <div className="text-center">
                      <Video className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <span className="text-sm text-gray-600 block">{video.fileName}</span>
                      <span className="text-xs text-gray-400">Uploaded Video</span>
                    </div>
                  </div>
                )}
                
                {/* Video Controls Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => removeVideo(index)}
                    className="absolute top-3 right-3 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  
                  {/* Video info */}
                  <div className="absolute bottom-3 left-3">
                    <div className="text-white text-sm">
                      <div className="font-medium">
                        {video.type === 'youtube' ? 'YouTube Video' : 'Uploaded Video'}
                      </div>
                      <div className="text-xs opacity-75">
                        Video {index + 1} of {formData.videos.length}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No videos state */}
        {(!formData.videos || formData.videos.length === 0) && (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
            <Video className="h-8 w-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No videos added yet</p>
            <p className="text-gray-400 text-xs">Upload video files or add YouTube links above</p>
          </div>
        )}
      </div>
    </div>
  );
};